// src/components/DealerAdmin.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ShowFlags {
  showLinkRegisterFamily: boolean;
  showListFamilies: boolean;
  showLinkSubscriptions: boolean;
  showLinkListTempSub: boolean;
  showPaypalTransactions: boolean;
  showPaypalNotifications: boolean;
  showUsageAnalysis: boolean;
  showDivDealerAndDistributor: boolean;
  showDivDealer: boolean;
  showDivDistributor: boolean;
  showAddWorkForce: boolean;
  showOfficeAccountant: boolean;
  showRegisterOffice: boolean;
  showCountryManager: boolean;
  showSearchEngine: boolean;
  showNewSale: boolean;
  showModifySale: boolean;
  showEntryCustomerSupport: boolean;
  showViewCustomerSupport: boolean;
  showPackagesForDispatch: boolean;
  showApproveDeliveryDockets: boolean;
  showPaymentsReceived: boolean;
  showCrelogix: boolean;
  showFinanceSale: boolean;
  showInvoiceList: boolean;
  showInstallationList: boolean;
  showCommissionPaymentDetails: boolean;
  showOrderPackages: boolean;
  showLeadCollection: boolean;
  showTelemarketing: boolean;
  showDistributorOrders: boolean;
  showModifyDongleStatus: boolean;
  showDongleReport: boolean;
  showSalesReport: boolean;
  showDNCSearch: boolean;
  addDNC: boolean;
  displayForDistributor: boolean;
  showFromDistributor: boolean;
}

interface Office {
  login: string;
  name: string;
  password?: string;
}

interface UserInfo {
  school_code: string;
  family_code: string;
  login: string;
  category: string;
}

const EL_DealerAdmin: React.FC = () => {
  const navigate = useNavigate();

  // State for user data
  const [userInfo, setUserInfo] = useState<UserInfo>({
    school_code: '',
    family_code: '',
    login: '',
    category: ''
  });

  // State for show flags
  const [showFlags, setShowFlags] = useState<ShowFlags>({
    showLinkRegisterFamily: true,
    showListFamilies: true,
    showLinkSubscriptions: true,
    showLinkListTempSub: false,
    showPaypalTransactions: false,
    showPaypalNotifications: false,
    showUsageAnalysis: true,
    showDivDealerAndDistributor: true,
    showDivDealer: true,
    showDivDistributor: true,
    showAddWorkForce: false,
    showOfficeAccountant: false,
    showRegisterOffice: false,
    showCountryManager: false,
    showSearchEngine: false,
    showNewSale: false,
    showModifySale: false,
    showEntryCustomerSupport: false,
    showViewCustomerSupport: false,
    showPackagesForDispatch: false,
    showApproveDeliveryDockets: false,
    showPaymentsReceived: false,
    showCrelogix: false,
    showFinanceSale: false,
    showInvoiceList: false,
    showInstallationList: false,
    showCommissionPaymentDetails: false,
    showOrderPackages: false,
    showLeadCollection: false,
    showTelemarketing: false,
    showDistributorOrders: false,
    showModifyDongleStatus: false,
    showDongleReport: false,
    showSalesReport: false,
    showDNCSearch: false,
    addDNC: false,
    displayForDistributor: false,
    showFromDistributor: false
  });

  // State for offices
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<string>('-Select-');

  // State for workforce dropdowns
  const [jrRole, setJrRole] = useState<string>('1');
  const [srRole, setSrRole] = useState<string>('2');
  const [ardCategory, setArdCategory] = useState<string>('');

  // State for error message
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/dealer-admin/');
        if (!response.ok) throw new Error('Failed to load data');

        const data = await response.json();
        if (data.success) {
          setUserInfo(data.user);
          setShowFlags(data.showFlags);
          setOffices(data.offices);
          setErrorMessage(data.errorMessage || '');
        } else {
          setErrorMessage(data.error || 'Failed to load data');
        }
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'An error occurred');
      }
    };

    fetchData();
  }, []);

  // Handle role selection
  const handleRoleChange = () => {
    let category = '';
    if (jrRole === '5') {
      category = '62';
    } else if (jrRole === '6') {
      category = '63';
    } else {
      category = jrRole + srRole;
    }

    setArdCategory(category);

    // Hide/show senior role dropdown based on junior role
    if (category === '62' || category === '63') {
      // Hide senior role dropdown
      document.getElementById('ddlSr')?.setAttribute('style', 'visibility: hidden;');
    } else {
      // Show senior role dropdown
      document.getElementById('ddlSr')?.setAttribute('style', 'visibility: visible;');
    }

    return true;
  };

  // Handle office selection
  const handleOfficeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOffice(e.target.value);
  };

  // Handle office login
  const handleOfficeLogin = () => {
    if (selectedOffice === '-Select-') return;

    const selected = offices.find(o => o.login === selectedOffice);
    if (selected) {
      // In a real app, you would handle authentication here
      // For now, just navigate to the login page with parameters
      navigate(`/login?officeCode=${userInfo.school_code}&loginID=${selected.login}&password=${selected.password}`);
    }
  };

  // Handle distributor home click
  const handleDistributorHomeClick = () => {
    // In a real app, this would call the logout endpoint
    fetch('/api/dealer-admin/logout-office/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    }).then(response => {
      if (response.ok) {
        // Redirect to home or login page
        navigate('/');
      }
    });
  };

  // JavaScript functions migrated from VB
  const fn_UnDone = () => {
    alert('Under Construction.');
    return false;
  };

  const fn_OpenNewWindow = (e: React.MouseEvent) => {
       e.preventDefault();
    window.open('/sales/new-sale', 'open_window', 'scrollbars, resizable, dependent, left=0, top=0, fullscreen=yes');
    return false;
  };

  const fn_ShowSalesReport = () => {
    const W = window.screen.availWidth - 10;
    const H = window.screen.availHeight - 80;
    const url = "/webadmin/reports/km-distributor-sales-report";
    window.open(url, "Show Sales Report", `width=${W},height=${H}`);
    return false;
  };

  const fn_AddDNCNumbers = () => {
    const URL = "/support/el-add-dnc-numbers-new?DNCAction=ADD";
    window.open(URL, "", "width=600,height=300");
    return false;
  };

  const ShowPPTransaction = (e: React.MouseEvent) => {
    e.preventDefault();
    const url = "/el-list-paypal-transactions";
    window.open(url, "Paypal Transactions", '');
    return false;
  };

  const ShowPPNotification = (e: React.MouseEvent) => {
       e.preventDefault();
    const url = "/el-list-paypal-transactions";
    window.open(url, "Paypal Transactions", '');
    return false;
  };

  // Show error message if present
  useEffect(() => {
    if (errorMessage) {
      alert(errorMessage);
    }
  }, [errorMessage]);

  return (
    <div style={{ width: '800px', height: '600px', backgroundImage: 'url(/Images/MasterBkg.png)' }}>
      {/* Top spacing */}
      <div style={{ height: '60px' }}></div>

      {/* Header */}
      <div id="div1" style={{ marginLeft: '42px', marginRight: '40px', fontFamily: 'Arial', fontSize: '11pt', textAlign:"left"}}>
        <table cellPadding="3" cellSpacing="0" border={0} style={{ width: '100%', color: 'Maroon', fontWeight: 'bold', fontSize: '20px', fontFamily: 'calibri', padding: '5px', height: '25px', backgroundColor: '#C1E0FF', border: '2px solid #80A0FF' }}>
          <tbody>
            <tr>
              <td style={{ width: '30%' }} align="left">
                {showFlags.showFromDistributor && (
                  <button
                    onClick={handleDistributorHomeClick}
                    style={{ background: 'none', border: 'none', color: 'Maroon', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Home
                  </button>
                )}
              </td>
              <td style={{ width: '70%' }}>Click on the following links to process:</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Main content */}
      <div id="divDealerAndDistributor" style={{
        marginLeft: '42px',
        marginRight: '40px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        height: '466px',
        textAlign:"left",
        display: showFlags.showDivDealerAndDistributor ? 'block' : 'none'
      }}>
        <table border={0} cellPadding={0} cellSpacing={0} style={{ width: '100%', border: '2px solid #80A0FF', borderTop: 'none' }}>
          <tbody>
            <tr>
              {/* Left column - Dealer */}
              <td style={{
                width: '420px',
                height: '385px',
                borderRight: 'solid 1px #80A0FF',
                display: showFlags.showDivDealer ? 'table-cell' : 'none',
                paddingLeft: '5px',
                paddingRight: '5px',
                verticalAlign: 'top'
              }}>
                <table id="tblDealer" style={{ width: '100%', marginTop: '8px' }} cellSpacing={0} cellPadding={2} border={0}>
                  <tbody>
                    {/* Register New Family */}
                    <tr style={{ display: showFlags.showLinkRegisterFamily ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px', height: '22px' }}>
                        <a href="/el-dealer-register-family" style={{ color: 'Maroon', textDecoration: 'none' }}>Register New Family</a>
                        <br />
                      </td>
                    </tr>

                    {/* List Families */}
                    <tr style={{ display: showFlags.showListFamilies ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px' }}>
                        <a href="/el-list-families" style={{ color: 'Maroon', textDecoration: 'none' }}>List Families</a>
                        <br />
                      </td>
                    </tr>

                    {/* List Subscriptions */}
                    <tr style={{ display: showFlags.showLinkSubscriptions ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px' }}>
                        <a href="/el-subscriptions" style={{ color: 'Maroon', textDecoration: 'none' }}>List Subscriptions</a>
                        <br />
                      </td>
                    </tr>

                    {/* List of data captured from Payment Website */}
                    <tr style={{ display: showFlags.showLinkListTempSub ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px', height: '22px' }}>
                        <a href="/el-list-temp-sub" style={{ color: 'Maroon', textDecoration: 'none' }}>
                          List of data captured from Payment Website
                        </a>
                        <br />
                      </td>
                    </tr>

                    {/* List Paypal Transactions */}
                    <tr style={{ display: showFlags.showPaypalTransactions ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px' }}>
                        <a href="#" onClick={ShowPPTransaction} style={{ color: 'Maroon', textDecoration: 'none' }}>List Paypal Transactions</a>
                        <br />
                      </td>
                    </tr>

                    {/* List Paypal Notifications */}
                    <tr style={{ display: showFlags.showPaypalNotifications ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px' }}>
                        <a href="#" onClick={ShowPPNotification} style={{ color: 'Maroon', textDecoration: 'none' }}>List Paypal Notifications</a>
                        <br />
                      </td>
                    </tr>

                    {/* Usage Analysis */}
                    <tr style={{ display: showFlags.showUsageAnalysis ? 'table-row' : 'none' }}>
                      <td style={{ width: '100%', paddingBottom: '8px' }}>
                        <a href="/el-dealer-usage-analysis" style={{ color: 'Maroon', textDecoration: 'none' }}>Usage Analysis</a>
                        <br />
                      </td>
                    </tr>

                    {/* Add Workforce */}
                    <tr>
                      <td style={{ paddingBottom: '8px', borderTop: 'solid 1px blue' }}>
                        <table id="tblAddWorkForce" width="100%" align="center" cellPadding={4} cellSpacing={0} border={0}
                          style={{
                            fontWeight: 'bold',
                            border: 'solid 1px blue',
                            display: showFlags.showAddWorkForce ? 'table' : 'none',
                            verticalAlign: 'top'
                          }}>
                          <tbody>
                            <tr>
                              <td colSpan={3} align="center" style={{ backgroundColor: '#CCCCFF' }}>Add Workforce</td>
                            </tr>
                            {/* Hidden rows from original VB - kept for completeness but not displayed */}
                            <tr style={{ display: 'none' }}>
                              <td align="left" valign="top" style={{ width: '38%', borderTop: 'solid 1px blue', whiteSpace: 'nowrap' }}>
                                <input type="radio" id="rdo1" name="rdoA" defaultChecked /><span id="spn1">Lead Collector</span>
                              </td>
                              <td align="left" valign="top" style={{ borderTop: 'solid 1px blue' }}>
                                <input type="radio" id="rdoR2" name="rdoB" defaultChecked />City Manager<br />
                              </td>
                              <td style={{ borderLeft: 'solid 1px blue', width: '80px', borderTop: 'solid 1px blue' }} rowSpan={7} align="center"></td>
                            </tr>
                            {/* Additional hidden rows... */}

                            {/* Visible row with dropdowns */}
                            <tr>
                              <td colSpan={3} align="center" valign="top">
                                <select
                                  id="ddlJr"
                                  onChange={handleRoleChange}
                                  style={{ width: '110px' }}
                                  value={jrRole}
                                >
                                  <option value="1">Lead Collector</option>
                                  <option value="2">Tele-Marketer</option>
                                  <option value="3">Trainer</option>
                                  <option value="4">Salesman</option>
                                  <option value="5">B.D.M.</option>
                                  <option value="6">Installer</option>
                                </select>

                                <select
                                  id="ddlSr"
                                  onChange={handleRoleChange}
                                  style={{ width: '112px', visibility: (ardCategory === '62' || ardCategory === '63') ? 'hidden' : 'visible' }}
                                  value={srRole}
                                >
                                  <option value="1">City Manager</option>
                                  <option value="2">Sales-Manager</option>
                                  <option value="3">Team Leader</option>
                                  <option value="4">Consultant</option>
                                </select>

                                &nbsp;
                                <button
                                  id="btnAddOtherLogins"
                                  style={{ width: '50px', fontSize: '10pt', fontWeight: 'bold' }}
                                  onClick={() => navigate(`/el-register-salesman?ARD=A&ARDCategory=${ardCategory}`)}
                                >
                                  Add
                                </button>
                                &nbsp;
                                <button
                                  id="btnModifyOtherLogins"
                                  style={{ width: '58px', fontSize: '10pt', fontWeight: 'bold' }}
                                  onClick={() => navigate(`/el-list-salesman?ARDCategory=${ardCategory}`)}
                                >
                                  Modify
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>

                    {/* Office Accountant */}
                    <tr>
                      <td style={{
                        width: '100%',
                        paddingBottom: '8px',
                        paddingLeft: '5px',
                        display: showFlags.showOfficeAccountant ? 'table-cell' : 'none'
                      }}>
                        <b>Office Staff:</b>&nbsp;&nbsp;&nbsp;
                        <a href="/el-register-office-accountant?ARD=A" style={{ color: 'Maroon', textDecoration: 'none' }}>Add</a>&nbsp;&nbsp;&nbsp;
                        <a href="/el-register-office-accountant?ARD=R" style={{ color: 'Maroon', textDecoration: 'none' }}>Modify</a>
                      </td>
                    </tr>

                    {/* Register Office */}
                    <tr>
                      <td style={{
                        paddingBottom: '8px',
                        display: showFlags.showRegisterOffice ? 'table-cell' : 'none'
                      }}>
                        <b>Office:</b>&nbsp;&nbsp;&nbsp;
                        <a href="/el-register-office" style={{ color: 'Maroon', textDecoration: 'none' }}>Register</a>&nbsp;&nbsp;&nbsp;&nbsp;
                        <select
                          id="ddlOfficeList"
                          onChange={handleOfficeSelect}
                          style={{ width: '140px' }}
                          value={selectedOffice}
                        >
                          <option value="-Select-">-Select-</option>
                          {offices.map(office => (
                            <option key={office.login} value={office.login}>{office.name}</option>
                          ))}
                        </select>
                        &nbsp;
                        <button
                          id="btnOK"
                          onClick={handleOfficeLogin}
                          style={{
                            width: '70px',
                            verticalAlign: 'bottom',
                            fontSize: '10pt',
                            fontWeight: 'bold'
                          }}
                        >
                          Login
                        </button>
                      </td>
                    </tr>

                    {/* Country Manager */}
                    <tr style={{ display: showFlags.showCountryManager ? 'table-row' : 'none' }}>
                      <td style={{ paddingBottom: '8px' }}>
                        <b>Country Manager:</b>&nbsp;&nbsp;
                        <a href="/el-register-salesman?ARD=A&ARDCategory=61" style={{ color: 'Maroon', textDecoration: 'none' }}>Register</a>
                        &nbsp;&nbsp;&nbsp;
                        <a href="/el-list-salesman?ARDCategory=61" style={{ color: 'Maroon', textDecoration: 'none' }}>Modify</a>
                        <br />
                      </td>
                    </tr>

                    {/* Search Engine */}
                    <tr style={{ display: showFlags.showSearchEngine ? 'table-row' : 'none' }}>
                      <td style={{ paddingBottom: '8px' }}>
                        <a href="/support/el-search-engine" style={{ color: 'Maroon', textDecoration: 'none' }}>Search Engine</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>

              {/* Right column - Distributor */}
              <td style={{
                width: '380px',
                paddingLeft: '5px',
                display: showFlags.showDivDistributor ? 'table-cell' : 'none',
                verticalAlign: 'top'
              }}>
                <div style={{ overflowY: 'auto', overflowX: 'hidden', height: '420px' }}>
                  <table border={0} cellPadding={5} cellSpacing={0} style={{ width: '100%', marginTop: '8px' }}>
                    <tbody>
                      {/* New Sale */}
                      <tr style={{ display: showFlags.showNewSale ? 'table-row' : 'none' }}>
                        <td style={{ width: '100%', paddingBottom: '6px' }}>
                          <a href="#" onClick={fn_OpenNewWindow} style={{ color: 'Maroon', textDecoration: 'none' }}>New Sale</a>
                          <br />
                        </td>
                      </tr>

                      {/* Modify Sale */}
                      <tr style={{ display: showFlags.showModifySale ? 'table-row' : 'none' }}>
                        <td style={{ width: '100%', paddingBottom: '8px' }}>
                          <a href="/sales/sales-modify/el-sales-modify" style={{ color: 'Maroon', textDecoration: 'none' }}>Sales</a>
                          <br />
                        </td>
                      </tr>

                      {/* View Customer Support */}
                      <tr style={{ display: showFlags.showViewCustomerSupport ? 'table-row' : 'none' }}>
                        <td style={{ width: '100%', paddingBottom: '8px' }}>
                          <a href="/support/el-list-of-tasks?FilterType=CustView" style={{ color: 'Maroon', textDecoration: 'none' }}>View Customer Support</a>
                          <br />
                        </td>
                      </tr>

                      {/* Entry Customer Support */}
                      <tr style={{ display: showFlags.showEntryCustomerSupport ? 'table-row' : 'none' }}>
                        <td style={{ width: '100%', paddingBottom: '8px' }}>
                          <a href="/support/el-list-of-tasks?FilterType=CustEntry" style={{ color: 'Maroon', textDecoration: 'none' }}>Entry Customer Support</a>
                          <br />
                        </td>
                      </tr>

                      {/* Packages for Dispatch */}
                      <tr style={{ display: showFlags.showPackagesForDispatch ? 'table-row' : 'none' }}>
                        <td style={{ width: '100%', paddingBottom: '8px' }}>
                          <a href="/sales/dockets/el-delivery-dockets" style={{ color: 'Maroon', textDecoration: 'none' }}>Packages for Dispatch</a>
                          <br />
                        </td>
                      </tr>

                      {/* Pre-Audit Sales */}
                      <tr style={{ display: showFlags.showApproveDeliveryDockets ? 'table-row' : 'none' }}>
                        <td style={{
                          width: '100%',
                          paddingBottom: '8px',
                          display: showFlags.displayForDistributor ? 'table-cell' : 'none'
                        }}>
                          <a href="/el-approve-delivery-dockets" style={{ color: 'Maroon', textDecoration: 'none' }}>Pre-Audit Sales</a>
                          <br />
                        </td>
                      </tr>

                      {/* Payments Received */}
                      <tr style={{ display: showFlags.showPaymentsReceived ? 'table-row' : 'none' }}>
                        <td style={{
                          width: '100%',
                          paddingBottom: '8px',
                          display: showFlags.displayForDistributor ? 'table-cell' : 'none'
                        }}>
                          <a href="/finance/payments/el-payment-received" style={{ color: 'Maroon', textDecoration: 'none' }}>Payments Received</a>
                          <br />
                        </td>
                      </tr>

                      {/* Crelogix */}
                      <tr style={{ display: showFlags.showCrelogix ? 'table-row' : 'none' }}>
                        <td style={{
                          width: '100%',
                          paddingBottom: '8px',
                          display: showFlags.displayForDistributor ? 'table-cell' : 'none'
                        }}>
                          <a href="/finance/crelogix/el-crelogix-main-menu" style={{ color: 'Maroon', textDecoration: 'none' }}>Crelogix</a>
                          <br />
                        </td>
                      </tr>

                      {/* Finance Sale */}
                      <tr style={{ display: showFlags.showFinanceSale ? 'table-row' : 'none' }}>
                        <td style={{ width: '100%', paddingBottom: '8px' }}>
                          <a href="/finance/el-families-to-finance" style={{ color: 'Maroon', textDecoration: 'none' }}>Finance Sale</a>
                          <br />
                        </td>
                      </tr>

                      {/* Invoice List */}
                      <tr style={{ display: showFlags.showInvoiceList ? 'table-row' : 'none' }}>
                        <td style={{
                          width: '100%',
                          paddingBottom: '8px',
                          display: showFlags.displayForDistributor ? 'table-cell' : 'none'
                        }}>
                          <a href="/finance/invoice/el-customer-invoice-list" style={{ color: 'Maroon', textDecoration: 'none' }}>Invoice List</a>
                          <br />
                        </td>
                      </tr>

                      {/* Installation List */}
                      <tr style={{ display: showFlags.showInstallationList ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A2" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>Installation List</a>
                          <br />
                        </td>
                      </tr>

                      {/* Commission Payment Details */}
                      <tr style={{ display: showFlags.showCommissionPaymentDetails ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A5" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>Commission Payment Details</a>
                          <br />
                        </td>
                      </tr>

                      {/* Order Dongle */}
                      <tr style={{ display: showFlags.showOrderPackages ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A7" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>OrderDongle</a>
                          <br />
                        </td>
                      </tr>

                      {/* Lead Collection */}
                      <tr style={{ display: showFlags.showLeadCollection ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A6" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>Lead Collection</a>
                          <br />
                        </td>
                      </tr>

                      {/* Telemarketing */}
                      <tr style={{ display: showFlags.showTelemarketing ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <table cellPadding={0} cellSpacing={0} border={0}>
                            <tbody>
                              <tr>
                                <td>
                                  <a id="A8" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>Telemarketing</a>
                                </td>
                              </tr>
                              <tr>
                                <td style={{ paddingTop: '10px' }}>
                                  <a id="A3" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>Prospective Client Appointments</a>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>

                      {/* Distributor Orders */}
                      <tr style={{ display: showFlags.showDistributorOrders ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A1" href="#" onClick={fn_UnDone} style={{ color: 'Maroon', textDecoration: 'none' }}>Distributor Orders</a>
                          <br />
                        </td>
                      </tr>

                      {/* Modify Dongle Status */}
                      <tr style={{ display: showFlags.showModifyDongleStatus ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A4" href="/sales/distributor/el-update-status" style={{ color: 'Maroon', textDecoration: 'none' }}>Modify Dongle Status</a>
                          <br />
                        </td>
                      </tr>

                      {/* Dongle Workflow Report */}
                      <tr style={{ display: showFlags.showDongleReport ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A9" href="/sales/distributor/el-dongle-workflow-summary" style={{ color: 'Maroon', textDecoration: 'none' }}>Dongle Work flow Report</a>
                        </td>
                      </tr>

                      {/* Sales Report */}
                      <tr style={{ display: showFlags.showSalesReport ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A10" href="#" onClick={fn_ShowSalesReport} style={{ color: 'Maroon', textDecoration: 'none' }}>Sales Report</a>
                        </td>
                      </tr>

                      {/* DNC Search */}
                      <tr style={{ display: showFlags.showDNCSearch ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A11" href="/support/el-add-dnc-numbers?DNC=Search" style={{ color: 'Maroon', textDecoration: 'none' }}>DNC Search</a>
                        </td>
                      </tr>

                      {/* Add DNC */}
                      <tr style={{ display: showFlags.addDNC ? 'table-row' : 'none' }}>
                        <td style={{ paddingBottom: '8px' }}>
                          <a id="A12" href="#" onClick={fn_AddDNCNumbers} style={{ color: 'Maroon', textDecoration: 'none' }}>Add DNC</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Hidden field for ARD Category */}
      <input type="hidden" id="hidARDCategory" value={ardCategory} />
    </div>
  );
};

export default EL_DealerAdmin;